import * as React from 'react';
import { HocOptions } from '@common/generic-hoc.types';
import { StylesProps } from '@theme/jss-types';
import { RouteChildrenProps } from 'react-router';
import GenericHoc from '@common/generic-hoc';
import PackageOptionsStyles from './packageOptions.styles';
import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Spin,
  Typography,
  message,
} from 'antd';
import { getPackageOptions, sendPaymentLinks, updatePackingMaterialsData } from '@src/network/dropoff.api';
import { PlusOutlined, CloseOutlined } from '@ant-design/icons';
import authHelper from '@src/auth/auth-helper';


interface PackageProps
  extends StylesProps<ReturnType<typeof PackageOptionsStyles>>, RouteChildrenProps {
  isPackageModalOpen: boolean;
  referenceNumber: string,
  setIsPackageModalOpen: (isOpen: boolean) => void;
  handleReloadDropOff: () => void;
  verifiedConsignmentDetail: Record<string, any>;
  showPaymentStatus: string;
  isInternational: boolean;
}

const PackagePage = (props : PackageProps) => {
  const {
    classes,
    isPackageModalOpen,
    setIsPackageModalOpen,
    referenceNumber,
    verifiedConsignmentDetail,
    handleReloadDropOff,
    showPaymentStatus,
    isInternational,
  } = props;
  const [packageOptionsData, setPackageOptionsData] = React.useState<any>();
  const [selectedPackageDetails, setSelectedPackageDetails] = React.useState<any[]>([]);
  const [showPaymentModal, setShowPaymentModal] = React.useState<boolean>(false);
  const [totalPayment, setTotalPayment] = React.useState<number>(0);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [rows, setRows] = React.useState<any[]>([]);
  const [form] = Form.useForm();
  const { Text } = Typography;
  const {
    username,
  } = authHelper.getUsernameAndLogo();


  const fetchPackageOption = async () => {
    const storage = window.localStorage;
    const hubCode = storage.getItem('hubCode') || '';
    const response = await getPackageOptions({
      hubCode,
      empCode: username,
    });
    if (response.isSuccess) {
      setPackageOptionsData(response.data);
    }
  };

  React.useEffect(() => {
    fetchPackageOption();
  }, []);

  const handleAddRow = () => {
    setRows([...rows, { itemCode: null, quantity: null }]);
  };

  React.useEffect(() => {
    if (isPackageModalOpen) {
      // Add a default row when modal opens
      handleAddRow();
    }
  }, [isPackageModalOpen]);
  const handleRowChange = (index: number, field: string, value: any) => {
    const updatedRows = [...rows];

    if (field === 'itemCode') {
    // Handle item selection
      const selectedItem = packageOptionsData.find((item: any) => item.itemCode === value);

      // Check if this item is already selected in another row and doesn't allow multiple quantities
      if (selectedItem?.allowMultiQty !== 'Y') {
        const itemAlreadySelected = rows.some(
          (r, i) => i !== index && r.itemCode === value,
        );

        if (itemAlreadySelected) {
          message.error(`${selectedItem?.itemDesc} is already selected and doesn't allow multiple quantities`);
          return;
        } // Auto-set quantity to 1 for non-multiple items
        updatedRows[index].quantity = 1;
      }

      updatedRows[index].itemCode = value;
    } else if (field === 'quantity') {
    // Handle quantity changes with validations
      if (updatedRows[index].itemCode) {
        const selectedItem = packageOptionsData.find((item: any) => item.itemCode
        === updatedRows[index].itemCode);

        // Validate against allowMultiQty
        if (selectedItem?.allowMultiQty !== 'Y' && value > 1) {
          message.error(`Only one quantity allowed for ${selectedItem.itemDesc}`);
          updatedRows[index].quantity = 1;
        } else if (value > selectedItem?.availableQuantity) {
          message.error(`Only ${selectedItem.availableQuantity} units available for ${selectedItem.itemDesc}`);
          updatedRows[index].quantity = selectedItem.availableQuantity;
        } else {
          updatedRows[index].quantity = value;
        }
      } else {
        updatedRows[index].quantity = value;
      }
    }
    setRows(updatedRows);

    // Update selectedPackageDetails for calculations
    if (packageOptionsData) {
      const validItems = updatedRows
        .filter((row) => row.itemCode && row.quantity)
        .map((row) => {
          const item = packageOptionsData.find((items: any) => items.itemCode === row.itemCode);
          if (item) {
            return {
              ...item,
              purchaseQuantity: row.quantity,
            };
          }
          return null;
        })
        .filter(Boolean);

      setSelectedPackageDetails(validItems);
    }
  };


  const calculateTotalPayment = () => {
    if (totalPayment) return totalPayment;
    let sum = 0;
    rows.forEach((row) => {
      if (row.itemCode && row.quantity) {
        const item = packageOptionsData.find((opt: any) => opt.itemCode === row.itemCode);
        if (item) {
          sum += (item.priceForEach * row.quantity);
        }
      }
    });

    setTotalPayment(sum);
    return sum;
  };
  const renderFooterPayment = () => {
    return (
      <div className={classes.footer}>
        <Form.Item>
          <Button
            className={classes.acceptButton}
            type="primary"
            htmlType="submit"
            disabled={selectedPackageDetails?.length === 0}
            onClick={async () => {
              const formValues = await form.validateFields();
              if (formValues.errorFields?.length) {
                return;
              }
              const apiData = {
                consignments: [
                  {
                    referenceNumber,
                    phone: formValues.phone,
                    email: formValues.email,
                  },
                ],
              };
              setLoading(true);
              const response = await sendPaymentLinks(apiData);
              setLoading(false);

              if (response.isSuccess) {
                handleReloadDropOff();
                setIsPackageModalOpen(false);
                setShowPaymentModal(false);
              } else {
                message.error(response?.errorMessage || 'Something went wrong!');
              }
            }}
          >
            Send Payment Link
            <Spin
              spinning={loading}
              size="small"
              style={{ color: 'white' }}
            />
          </Button>
        </Form.Item>
        <Text type="danger" className={classes.additionalAmount}>
          &nbsp;
          Additional Payment:
          &nbsp;
          {calculateTotalPayment()}
        </Text>
      </div>
    );
  };

  const closeModal = () => {
    setIsPackageModalOpen(false);
    setSelectedPackageDetails([]);
    setRows([]);
  };

  const renderModal = () => {
    return (
      <Modal
        className={classes.modal}
        open={isPackageModalOpen}
        onCancel={closeModal}
        closeIcon={null}
        onOk={() => setIsPackageModalOpen(false)}
        title={(
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', marginLeft: '10px',
          }}
          >
            <div>Add Packaging</div>
            <Button
              className={classes.proceedButton}
              type="primary"
              htmlType="submit"
              disabled={
              rows.length === 0 || !rows.some((row) => row.itemCode && row.quantity) || loading
            }
              onClick={async () => {
                const formValues = await form.validateFields();
                if (formValues.errorFields?.length) {
                  return;
                }
                // Continue with your existing logic here
                const packingMaterial = selectedPackageDetails?.map((item) => {
                  return {
                    ...item,
                    price: item.purchaseQuantity * item.priceForEach,
                  };
                });

                const apiData = {
                  consignments: [
                    {
                      referenceNumber,
                      courierType: verifiedConsignmentDetail?.loadType,
                      noOfPieces: 1,
                      VAS: {
                        isPacking: true,
                        packingDetails: {
                          packingMaterial,
                          price: calculateTotalPayment(),
                        },
                      },
                      isInternational,
                    },
                  ],
                };

                setLoading(true);
                const response = await updatePackingMaterialsData(apiData);
                setLoading(false);

                if (response.isSuccess && showPaymentStatus === 'PENDING') {
                  handleReloadDropOff();
                  setIsPackageModalOpen(false);
                } else if (response.isSuccess) {
                  setShowPaymentModal(true);
                } else {
                  message.error(response?.errorMessage || 'Something went wrong!');
                }
              }}
            >
              Proceed
              <Spin
                spinning={loading}
                size="small"
                style={{ color: 'white' }}
              />
            </Button>
          </div>
        )}
        footer={null}
        destroyOnClose
        maskClosable={false}
      >
        <div className={classes.customCloseButton} onClick={closeModal}>
          <CloseOutlined />
        </div>
        <div className={classes.warningNote}>
          <div className={classes.warningText}>
            Please note that adding packaging details will incur additional cost
          </div>
        </div>

        <Row className={classes.headerRow}>
          <Col span={8} className={classes.headerCol}>
            <div className={classes.columnHeader}>Packaging Material</div>
          </Col>
          <Col span={8} className={classes.headerCol}>
            <div className={classes.columnHeader}>Quantity Required</div>
          </Col>
          <Col span={8} className={classes.headerCol}>
            <div className={classes.columnHeader}>Unit Price</div>
          </Col>
        </Row>

        <Form form={form} layout="vertical">
          {rows.map((row, index) => (
            <Row key={row.itemCode || index} gutter={16} className={classes.inputRow}>
              <Col span={10}>
                {' '}
                {/* Increased the span from 8 to 10 for product name */}
                <Select
                  placeholder="Select Item"
                  options={packageOptionsData?.map((data) => ({
                    value: data.itemCode,
                    label: (
                      <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {data.itemDesc}
                      </div>
                    ),
                  }))}
                  onChange={(value) => handleRowChange(index, 'itemCode', value)}
                  value={row.itemCode}
                  style={{ maxWidth: '100%' }}
                />
              </Col>
              <Col span={6}>
                {' '}
                {/* Reduced the span for quantity */}
                <Col span={6}>
                  <InputNumber
                    placeholder="Quantity"
                    min={0}
                    max={row.itemCode
                      ? packageOptionsData.find((item) => item.itemCode
                      === row.itemCode)?.availableQuantity : undefined}
                    onChange={(value) => handleRowChange(index, 'quantity', value)}
                    value={row.quantity}
                    disabled={row.itemCode
                    && packageOptionsData.find((item) => item.itemCode === row.itemCode)?.allowMultiQty !== 'Y'
                    && row.quantity >= 1}
                  />
                </Col>
              </Col>
              <Col span={8}>
                <div>
                  {row.itemCode
                    ? packageOptionsData.find((item) => item.itemCode
                    === row.itemCode)?.priceForEach.toFixed(2)
                    : '0.00'}
                </div>
              </Col>
            </Row>
          ))}

          <div className={classes.addMoreButtonWrapper}>
            <Button
              type="text"
              onClick={handleAddRow}
              className={classes.addMoreButton}
              icon={<PlusOutlined className={classes.addMoreIcon} />}
            >
              Add More
            </Button>
          </div>
        </Form>
      </Modal>
    );
  };

  const renderPaymentModal = () => {
    return (
      <Modal
        className={classes.modal}
        open={isPackageModalOpen}
        onCancel={closeModal}
        title="Add Packaging Material"
        footer={null}
        maskClosable={false}
        destroyOnClose
      >
        <div className={classes.selectLabel}>
          Phone
        </div>
        <Form.Item
          name={['phone']}
          rules={[{
            required: true,
            message: 'Required',
          }]}
          className={classes.formItem}
          initialValue={verifiedConsignmentDetail?.originDetails?.phone || ''}
        >
          <Input
            className={classes.inputQuantity}
            placeholder="Phone Number"
          />
        </Form.Item>
        <div className={classes.selectLabel}>
          Email
        </div>
        <Form.Item
          name={['email']}
          rules={[{
            required: true,
            message: 'Required',
          },
          {
            type: 'email',
          },
          ]}
          initialValue={verifiedConsignmentDetail?.originDetails?.email || ''}
          className={classes.formItem}
        >
          <Input
            className={classes.inputQuantity}
            placeholder="Email"
          />
        </Form.Item>
        {renderFooterPayment()}
      </Modal>
    );
  };

  return (
    <Form className={classes.main} form={form}>
      {showPaymentModal ? renderPaymentModal() : renderModal()}
    </Form>
  );
};

const hocConfig: HocOptions = {
  connectRedux: {
    useRedux: true,
  },
  connectJss: {
    useJss: true,
    styleSheet: PackageOptionsStyles,
  },
  connectRouter: true,
};

export default GenericHoc(hocConfig)(PackagePage);
